import { BehaviorSubject, Observable, of, Subject } from "rxjs";
import { Polygon } from "geojson";
import { inject, Injectable } from "@angular/core";
import { IMetadataImage } from "../../../interfaces/i-metadataimage";
import { GeoService } from "../geo.service";
import { IGalleryFilter } from "../../../interfaces/i-gallery-filter";
import { IGalleryStatus } from "../../../interfaces/i-gallery-status";
import { ImagesService } from "../images.service";
import { IGalleryCollection } from "../../../interfaces/i-gallery-collection";
import { LocalStorageService } from "../local-storage.service";
import { IGalleryLayer } from "../../../interfaces/i-gallery-layer";
import { MapService } from "../map.service";
import { ViewerConstants } from "../../../../shared/model/viewer-constants";
import { VIEWER_SERVICE } from "../viewer-services/inject-base-viewer.service";

@Injectable()
export class ImageGalleryService {
  // region Definitions
  protected mapService = inject(MapService);

  protected storageService = inject(LocalStorageService);

  protected viewerService = inject(VIEWER_SERVICE);

  GSD = {
    ultra: {
      max: ImagesService.GSD_THRESHOLD,
      min: 0,
    },
    high: {
      max: 50,
      min: ImagesService.GSD_THRESHOLD,
    },
  };

  private allImages: IMetadataImage[] = [];

  private filteredImages: IMetadataImage[] = [];

  private closeDialogSubject = new Subject<boolean>();

  public closeDialog$ = this.closeDialogSubject.asObservable();

  private allImagesSubject = new Subject<IMetadataImage[]>();

  public allImages$ = this.allImagesSubject.asObservable();

  private filteredImagesSubject = new Subject<IMetadataImage[]>();

  public filteredImages$ = this.filteredImagesSubject.asObservable();

  private statusSubject = new BehaviorSubject<IGalleryStatus>(null);

  public status$ = this.statusSubject.asObservable();

  private fullscreenImageSubject = new BehaviorSubject<IMetadataImage>(null);

  public fullscreenImage$ = this.fullscreenImageSubject.asObservable();

  private fullscreenImageSerialNumberSubject = new BehaviorSubject<number>(null);

  public fullscreenImageSerialNumber$ = this.fullscreenImageSerialNumberSubject.asObservable();

  protected isFullscreenModeOnSubject = new BehaviorSubject<boolean>(false);

  public isFullscreenModeOn$ = this.isFullscreenModeOnSubject.asObservable();

  private selectedCollectionSubject = new BehaviorSubject<IGalleryCollection>(null);

  public selectedCollection$ = this.selectedCollectionSubject.asObservable();

  private collectionsSubject = new BehaviorSubject<IGalleryCollection[]>([]);

  public collections$ = this.collectionsSubject.asObservable();

  private selectedFilterSubject = new BehaviorSubject<IGalleryFilter>(null);

  public selectedFilter$ = this.selectedFilterSubject.asObservable();

  private obliqueAvailableSubject = new BehaviorSubject<boolean>(true);

  public obliqueAvailable$ = this.obliqueAvailableSubject.asObservable();

  private displayGallerySubject = new BehaviorSubject<boolean>(false);

  public displayGallery$ = this.displayGallerySubject.asObservable();

  // endregion

  // region Handle Gallery Status

  setGalleryStatus(status: IGalleryStatus) {
    this.saveGalleryStatus(status);
    this.setCollections(status && status.collections ? status.collections : null);
    this.statusSubject.next(status);
  }

  setCollections(collections: IGalleryCollection[]) {
    this.collectionsSubject.next(collections);
  }

  setSelectedCollection(collection: IGalleryCollection) {
    this.saveCollection(collection);
    this.selectedCollectionSubject.next(collection);
  }

  getGalleryStatus(): IGalleryStatus {
    return this.statusSubject.getValue();
  }

  /**
   * Calculate status and collections
   * @param images
   * @param extent
   * @param center
   * @param address
   */
  calculateInitStatus(
    images: IMetadataImage[],
    extent: Polygon,
    center: number[],
    address: string,
  ): Observable<IGalleryStatus> {
    const extentWKT = GeoService.polygonToWkt(extent);
    const processImages = this.formatImages(images, extentWKT);

    /* Calculate layers years and filters available */

    const collections = this.calculateCollections(processImages);
    const status = {
      extent,
      center,
      address,
      collections,
      years: [...new Set(collections.reduce((years, collection) => years.concat(collection.year), []))],
      layers: [...new Set(collections.reduce((layers, collection) => layers.concat(collection.layers), []))],
    };

    return of(status);
  }

  /**
   * Select collection
   * Update images
   * Calculate filter for this collection and apply filter for collection images
   * Update filtered images
   * @param collection
   */
  selectCollection(collection: IGalleryCollection): Observable<IGalleryCollection> {
    this.setSelectedCollection(collection);

    if (collection) {
      // select filter for this collection, get from storage or get default values for this collection
      const selectedFilter = this.selectFilters(collection);
      // we must save the filters to recover the next time we open the gallery or change the year
      this.setSelectedFilter(selectedFilter);
      // apply this filters to collection images
      const filteredImages = this.getFilterImages(collection.images, selectedFilter);
      // update total images and filtered images
      this.updateTotalImages(collection.images);
      this.updateFilteredImages(filteredImages);
    } else {
      this.setSelectedFilter(null);
      this.updateTotalImages([]);
      this.updateFilteredImages([]);
    }

    return of(collection);
  }

  // endregion

  // region Handle Filters
  setSelectedFilter(filter: IGalleryFilter): void {
    this.saveFilter(filter);
    this.selectedFilterSubject.next(filter);
  }

  /**
   * Update filter and apply to collection images
   * @param selectedFilter
   */
  updateFilter(selectedFilter: IGalleryFilter): void {
    this.setSelectedFilter(selectedFilter);
    // apply this filters to collection images
    const filteredImages = this.getFilterImages(this.selectedCollectionSubject.getValue().images, selectedFilter);

    // update total images and filtered images
    this.updateFilteredImages(filteredImages);
  }

  /**
   * Return a image array filtered by gallery filter
   * Filter by layer category, range resolution (ultra-high) or orientation
   * @param images
   * @param selectedFilter
   */
  getFilterImages(images: IMetadataImage[], selectedFilter: IGalleryFilter): IMetadataImage[] {
    let filteredImages = [...images];
    if (selectedFilter.layername) {
      filteredImages = this.filterImageByType(filteredImages, selectedFilter.layername);
    }
    if (selectedFilter.resolution) {
      filteredImages = filteredImages.filter((img) => {
        const gsd = img.gsd * 100;
        return gsd < this.GSD[selectedFilter.resolution].max && gsd > this.GSD[selectedFilter.resolution].min;
      });
    }
    if (selectedFilter.orientation) {
      filteredImages = ImagesService.isNadir(selectedFilter.orientation)
        ? filteredImages.filter((img) => ImagesService.isNadir(img.orientation))
        : filteredImages.filter((img) => img.orientation === selectedFilter.orientation);
    }
    return filteredImages;
  }

  /**
   * Calculate an return filter for this collection
   * If we have a previous filter saved we try to use this filter if we can apply for this collection
   * If we dont have a previous filter we calculate the default filter that we should show
   * @param collection
   */
  selectFilters(collection: IGalleryCollection): IGalleryFilter {
    const savedFilter = this.recoverFilter();
    const allowUseSavedFilter = this.collectionCanUseFilter(collection, savedFilter);
    let selectedLayer = null;
    let selectedResolution = null;
    let selectedOrientation = null;
    if (!savedFilter || !allowUseSavedFilter) {
      // if the collection layer only have one layer we preselect this if not we dont select anything
      selectedLayer = collection.types.length === 1 ? collection.types[0].name : null;
      const layer = collection.types.find((type) => type.name === selectedLayer);
      // if the collection resolutions only have one value we preselect this if not we dont select anything
      selectedResolution = layer && layer.resolutions.length === 1 ? layer.resolutions[0] : null;
      // if we dont have anything saved the orientation always null
      selectedOrientation = null;
    } else {
      // if the current collection have the saved layer we select this ifnot get the value from collection layer
      // if the collection layer only have one layer we preselect this if not we dont select anything
      const savedLayer = collection.types.find((type) => type.name === savedFilter.layername);

      if (savedLayer) {
        selectedLayer = savedLayer.name;
      } else if (collection.types.length === 1) {
        selectedLayer = collection.types[0].name;
      }
      const layer = collection.types.find((type) => type.name === selectedLayer);
      // if we have a selected layer and the collection include this resolution, we get the save resolution
      // if we have a selected layer and dont have the stored gsd we select the gsd if we only have one value
      if (layer && layer.resolutions.length === 1) {
        [selectedResolution] = layer.resolutions;
      } else if (layer && layer.resolutions.includes(savedFilter.resolution)) {
        selectedResolution = savedFilter.resolution;
      }

      // if the current collection have the current orientation or if is nadir have ony of nadir we return the saved orientation
      if (!selectedResolution || !layer.orientations[selectedResolution]) {
        selectedOrientation = null;
      } else if (
        layer.orientations[selectedResolution].includes(ImagesService.normalizeOrientation(savedFilter.orientation))
      ) {
        selectedOrientation = savedFilter.orientation;
      }
    }
    return {
      layername: selectedLayer,
      resolution: selectedResolution,
      orientation: selectedOrientation,
    };
  }

  /**
   * Check if we can use filter for collection
   * @param collection
   * @param filter
   */
  collectionCanUseFilter(collection: IGalleryCollection, filter: IGalleryFilter) {
    return filter && filter.layername && collection.layers.includes(filter.layername);
  }

  updateFilteredImages(images: IMetadataImage[]): void {
    this.filteredImages = images;
    this.filteredImagesSubject.next(images);
  }

  updateTotalImages(images: IMetadataImage[]): void {
    this.allImages = images;
    this.allImagesSubject.next(images);
  }

  changeLayerFilter(category: string): void {
    const selectedFilter = this.selectedFilterSubject.getValue();

    if (!category) {
      selectedFilter.layername = null;
      selectedFilter.orientation = null;
      selectedFilter.resolution = null;
    } else {
      const selectedCollection = this.selectedCollectionSubject.getValue();
      const selectedLayer = this.getCollectionCategory(selectedCollection, category);
      if (selectedCollection.types.length === 1) {
        selectedFilter.layername = selectedCollection.types[0].name;
      } else {
        selectedFilter.layername = selectedFilter.layername !== category ? category : null;
      }

      // if we dont have any layer selected we dont select any resolution
      selectedFilter.resolution = selectedFilter.layername ? this.defaultResolution(selectedLayer) : null;

      selectedFilter.orientation = null;
    }

    this.updateFilter(selectedFilter);
  }

  changeResolutionFilter(resolution: string): void {
    const selectedFilter = this.selectedFilterSubject.getValue();
    selectedFilter.resolution = selectedFilter.resolution !== resolution ? resolution : null;

    selectedFilter.orientation = null;

    this.updateFilter(selectedFilter);
  }

  getCollectionCategory(collection: IGalleryCollection, category: string): IGalleryLayer {
    return collection ? collection.types.find((type) => type.name === category) : null;
  }

  /**
   * Get default resolution for layer
   * @param layer
   */
  defaultResolution(layer: IGalleryLayer): string {
    if (!layer || layer.resolutions.length > 1) {
      return null;
    }
    return layer.resolutions[0];
  }

  changeOrientationFilter(orientation: string): void {
    const selectedFilter = this.selectedFilterSubject.getValue();
    selectedFilter.orientation = selectedFilter.orientation !== orientation ? orientation : null;
    this.updateFilter(selectedFilter);
  }

  // endregion

  // region Images functions
  /**
   * Add preview url to each image
   * Remove vertical/infrared orientations
   * sort by capture date
   * @param images
   * @param wkt extent geographic coordinates
   */
  formatImages(images: IMetadataImage[], wkt: string): IMetadataImage[] {
    return ImagesService.sortImagesByCaptureDate(
      images
        .map((img) => this.populateImageUrl(img, wkt))
        .filter((img) => img.previewTileURl)
        .filter(
          (img) =>
            ImagesService.obliqueOrientation
              .concat(ImagesService.nadirOrientation)
              .filter((orientation) => img.orientation.endsWith(orientation)).length,
        ),
    );
  }

  populateImageUrl(img: IMetadataImage, wkt: string): IMetadataImage {
    const zoomValue = this.getZoomValue(img);

    img.previewTileURl = this.mapService.extractImagesUrl(
      img.layername,
      zoomValue,
      null,
      wkt,
      "one",
      null,
      "all",
      "crop",
      "yes",
      img.image_id,
    );
    img.fullScreenTileURl = this.mapService.extractImagesUrl(
      img.layername,
      0,
      null,
      wkt,
      "one",
      null,
      "all",
      "crop",
      "yes",
      img.image_id,
    );

    return img;
  }

  getThumbnailURLForImage(img: any, wkt: string): IMetadataImage {
    img.fullScreenTileURl = this.mapService.extractOrthoImageUrl(img.source_layer, img.child_aoi, wkt);

    return img;
  }

  getThumbnailURL(layerName: string, childAoi: string, wkt: string) {
    return this.mapService.extractOrthoImageUrl(layerName, childAoi, wkt);
  }

  getZoomValue(image: IMetadataImage): number {
    const isUltraOrHigh =
      (image.source_layer.includes(ImagesService.ULTRA) || image.source_layer.includes(ImagesService.HIGH)) &&
      !image.source_layer.includes("-g");
    const mapZoom = this.viewerService.getZoom();

    switch (true) {
      case isUltraOrHigh && mapZoom >= 19:
        return 2;
      case !isUltraOrHigh && mapZoom >= 19:
        return 1;
      case isUltraOrHigh && mapZoom < 19:
        return 3;
      case !isUltraOrHigh && mapZoom < 19:
        return 2;
      default:
        return 1;
    }
  }

  // endregion

  // region Full screen
  showFullscreen(image: IMetadataImage, serialNumber: number): void {
    this.fullscreenImageSubject.next(image);
    this.fullscreenImageSerialNumberSubject.next(serialNumber);
    this.isFullscreenModeOnSubject.next(true);
    this.storageService.set("fullscreen", image);
    this.storageService.set("serialNumber", `${serialNumber}`);
  }

  closeFullscreen(): void {
    this.fullscreenImageSubject.next(null);
    this.fullscreenImageSerialNumberSubject.next(null);
    this.isFullscreenModeOnSubject.next(false);
    this.storageService.remove("fullscreen");
    this.storageService.remove("serialNumber");
  }

  closeDialogGallery(): void {
    this.closeDialogSubject.next(true);
  }

  showAdjacentFullscreenImage(shift: number): void {
    const currentIndex = this.filteredImages.indexOf(this.fullscreenImageSubject.getValue());
    const newIndex = currentIndex + shift;
    let serialNumber;
    if (newIndex === this.filteredImages.length) {
      serialNumber = 0;
    } else if (newIndex < 0) {
      serialNumber = this.filteredImages.length - 1;
    } else {
      serialNumber = newIndex;
    }
    this.fullscreenImageSerialNumberSubject.next(serialNumber);
    this.fullscreenImageSubject.next(this.filteredImages[this.fullscreenImageSerialNumberSubject.getValue()]);
    this.storageService.set("fullscreen", this.fullscreenImageSubject.getValue());
    this.storageService.set("serialNumber", `${this.fullscreenImageSerialNumberSubject.getValue()}`);
  }

  getTotalAmountOfFilteredImages(): number {
    return this.filteredImages.length;
  }

  // endregion

  // region Persist Data
  /**
   * Return collection previously stored in local storage
   */
  recoverSelectedCollection(): IGalleryCollection {
    return this.storageService.get("selected-collection");
  }

  /**
   * Save selected collection on local storage
   * Only if the collection has data
   * @param collection
   */
  saveCollection(collection: IGalleryCollection): boolean {
    return collection ? this.storageService.set("selected-collection", collection) : false;
  }

  /**
   * Save selected filter on local storage
   * Only if the filter is not null
   * @param selectedFilter
   */
  saveFilter(selectedFilter: IGalleryFilter): boolean {
    return selectedFilter ? this.storageService.set("filter", selectedFilter) : false;
  }

  saveGalleryStatus(status: IGalleryStatus): boolean {
    return this.storageService.set("gallery-status", status);
  }

  recoverFilter(): IGalleryFilter {
    return this.storageService.get("filter");
  }

  recoverGalleryStatus(): IGalleryStatus {
    return this.storageService.get("gallery-status");
  }

  isDockOpen(): boolean {
    return this.storageService.get("galleryDockOpen");
  }

  setDockOpen(open: boolean) {
    if (open) {
      this.storageService.set("galleryDockOpen", true);
      this.setDialogOpen(false);
    } else {
      this.storageService.remove("galleryDockOpen");
    }
  }

  setDialogOpen(open: boolean) {
    if (open) {
      this.setDockOpen(false);
      this.storageService.set("galleryDialogOpen", true);
    } else {
      this.storageService.remove("galleryDialogOpen");
    }
  }

  removeGalleryData() {
    this.storageService.remove("collection");
    this.storageService.remove("gallery-status");
    this.storageService.remove("filter");
    this.storageService.remove("filter-scroll");
    this.storageService.remove("filters");
    this.storageService.remove("location");
    this.storageService.remove("isDockingOpen");
    this.storageService.remove("mapCenter");
    this.storageService.remove("extent");
    this.storageService.remove("scroll");
    this.storageService.remove("zoom");
    this.storageService.remove("isGalleryOpen");
    this.storageService.remove("galleryDialogOpen");
    this.storageService.remove("galleryDockOpen");
    this.storageService.remove("serialNumber");
    this.storageService.remove("fullscreen");
    this.storageService.remove("selected-collection");
  }

  setObliqueAvailable(obliqueAvailable: boolean) {
    this.obliqueAvailableSubject.next(obliqueAvailable);
  }

  setDisplayGallery(displayGallery: boolean) {
    this.displayGallerySubject.next(displayGallery);
  }

  getImageSizeQueryParameters(width: number, height: number): string {
    if (this.mapService.getApiVersion() === ViewerConstants.API_VERSION_2) {
      return `&destination-dimensions=${width},${height},fill`;
    } else {
      return `&width=${width}&height=${height}`;
    }
  }

  // endregion

  viewImageOnMap(image: IMetadataImage): void {
    this.viewerService.show3DImage(image);
    if (this.isFullscreenModeOnSubject.getValue()) {
      this.closeFullscreen();
    }
    this.closeDialogGallery();
  }

  /**
   * Get collection to show when the gallery start
   * If we have a collection saved that match with any of the available collections we select that
   * If not we select the collection year that match with the image/layer that we see on the screen
   * @param collections
   */
  getInitCollection(collections: IGalleryCollection[]): IGalleryCollection {
    let selectedYear = null;
    if (this.recoverSelectedCollection()) {
      selectedYear = this.recoverSelectedCollection().year;
    } else {
      selectedYear = +this.viewerService.getVisibleDateTimeForPoint(this.getGalleryStatus().center);
    }

    const selectedCollection = collections.find((item) => item.year === selectedYear);
    return selectedCollection || collections[0];
  }

  /**
   * Return an image array filtered by type
   * Filter by layer category
   * @param images
   * @param type
   */
  filterImageByType(images: IMetadataImage[], type: string): IMetadataImage[] {
    const layersNames = ViewerConstants.COLLECTION_TYPE_LAYERS[type];
    return images.filter((i) => layersNames.some((l) => i.source_layer.includes(l)));
  }

  calculateCollections(imgs: IMetadataImage[]): IGalleryCollection[] {
    {
      const groupByYear = imgs.reduce((storage, image: IMetadataImage) => {
        const year = +image.capture_date.split("-")[0];
        storage[year] = storage[year] || [];
        storage[year].push(image);
        return storage;
      }, {});
      const collections = [];
      // Populate data like layer, resolutions and orientations in each collection
      Object.entries(groupByYear).forEach(([key, value]) => {
        const images = ImagesService.sortImagesByCaptureDate(value as IMetadataImage[]);
        const layers = ImagesService.getCollectionTypes(images);
        const orientations = ImagesService.getOrientations(images);
        const types = layers.map((layer) => {
          const layerImages = ImagesService.filterByCollectionType(images, layer);
          const layerResolutions = ImagesService.getResolutions(layerImages);
          const availableOrientations = layerImages.reduce((storage, image: IMetadataImage) => {
            const resolution = ImagesService.getImageResolution(image);
            storage[resolution] = storage[resolution] || [];
            const orientation = ImagesService.normalizeOrientation(image.orientation);
            if (!storage[resolution].includes(orientation)) {
              storage[resolution].push(orientation);
            }
            return storage;
          }, []);
          return {
            name: layer,
            resolutions: layerResolutions,
            orientations: availableOrientations,
          };
        });
        const collection = {
          year: +key,
          images,
          layers,
          types,
          orientations,
        };
        collections.push(collection);
      });
      return collections.sort((c1, c2) => c2.year - c1.year);
    }
  }
}
